// components/VideoPlayer/SubtitleSettings/SubtitleSettingsPanel.jsx
import React, { useState, useEffect } from "react";
import ReactDOM from "react-dom";
import {
  useSubtitleSettings,
  DISPLAY_MODES,
} from "../../../context/SubtitleSettingsContext";
import { useVideo } from "../../../context/VideoContext";
import { useSelectedSubtitles } from "../../../context/SelectedSubtitlesContext";
import SubtitleItem from "./SubtitleItem";
import "./SubtitleSettingsPanel.css";
import { getLanguageName } from "../../../utils/languages";
import { getPortalContainer } from "../../../utils/portalContainer";

const SubtitleSettingsPanel = ({ onClose }) => {
  const {
    setShowSelectorPanel,
    setShowStylePanel,
    resetToDefault,
    settings,
    updateSubtitleSetting,
  } = useSubtitleSettings();

  const { showSubtitles, setShowSubtitles, isFullscreen, isIOS } = useVideo();
  const { updateSubtitleLanguage, activeSubtitles } = useSelectedSubtitles();

  // تشخیص موبایل
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 480);
    };
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  const getDisplayModeText = (mode) => {
    switch (mode) {
      case DISPLAY_MODES.SHOW:
        return "نمایش";
      case DISPLAY_MODES.HIDDEN:
        return "پنهان";
      case DISPLAY_MODES.UNDERCOVER:
        return "زیر کاور";
      default:
        return "";
    }
  };

  const handleReset = () => {
    resetToDefault();
    onClose();
  };

  const toggleSubtitles = () => {
    setShowSubtitles(!showSubtitles);
  };

  const handleLanguageChange = (subNumber, newLanguage) => {
    updateSubtitleSetting(subNumber, "language", newLanguage);
    const activeSub = activeSubtitles[`subtitle${subNumber}`];
    if (activeSub) {
      updateSubtitleLanguage(subNumber, newLanguage);
    }
  };

  const handleDisplayModeChange = (subNumber, newMode) => {
    updateSubtitleSetting(subNumber, "displayMode", newMode);
  };

  const getCurrentLanguage = (subNumber) => {
    const activeSub = activeSubtitles[`subtitle${subNumber}`];
    if (activeSub && activeSub.language) {
      return getLanguageName(activeSub.language);
    }
    return getLanguageName(settings[`subtitle${subNumber}`]?.language);
  };

  const getCurrentMode = (subNumber) => {
    return getDisplayModeText(settings[`subtitle${subNumber}`]?.displayMode);
  };

  return ReactDOM.createPortal(
    <div
      className="ssp-overlay"
      onClick={(e) => {
        e.stopPropagation();
      }}
    >
      <div className="ssp-panel">
        <div className="ssp-header">
          <h3>⚙️ تنظیمات زیرنویس</h3>
          <button className="ssp-close-btn" onClick={onClose}>
            ✕
          </button>
        </div>

        <div className="ssp-content">
          {/* سوییچ کنترل کلی زیرنویس */}
          <div className="ssp-global-switch">
            <div className="ssp-switch-info">
              <span className="ssp-switch-icon">📺</span>
              <span className="ssp-switch-label">نمایش زیرنویس</span>
            </div>
            <div className="ssp-switch">
              {!isMobile && (
                <span className={`ssp-switch-state ${showSubtitles ? "ssp-state-on" : ""}`}>
                  {showSubtitles ? "فعال" : "غیرفعال"}
                </span>
              )}
              <button
                className={`ssp-switch-btn ${showSubtitles ? "ssp-active" : ""}`}
                onClick={toggleSubtitles}
                role="switch"
                aria-checked={showSubtitles}
              >
                <span className="ssp-switch-handle"></span>
              </button>
            </div>
          </div>

          {/* دو گزینه اصلی کنار هم */}
          <div className="ssp-main-options">
            <button
              className="ssp-main-option"
              onClick={(e) => {
                e.stopPropagation();
                setShowSelectorPanel(true);
                onClose();
              }}
            >
              <span className="ssp-option-icon">🔍</span>
              <span className="ssp-option-text">انتخاب زیرنویس</span>
              {!isMobile && <span className="ssp-option-arrow">‹</span>}
            </button>

            <button
              className="ssp-main-option"
              onClick={(e) => {
                e.stopPropagation();
                setShowStylePanel(true);
                onClose();
              }}
            >
              <span className="ssp-option-icon">🎨</span>
              <span className="ssp-option-text">استایل زیرنویس</span>
              {!isMobile && <span className="ssp-option-arrow">‹</span>}
            </button>
          </div>

          {/* جداکننده */}
          <div className="ssp-divider">
            <span>تنظیمات سریع</span>
          </div>

          {/* دو زیرنویس کنار هم */}
          <div
            className={`ssp-quick-settings ${!showSubtitles ? "ssp-disabled" : ""}`}
          >
            <SubtitleItem
              subNumber={1}
              title="زیرنویس یک"
              currentLanguage={getCurrentLanguage(1)}
              currentMode={getCurrentMode(1)}
              disabled={!showSubtitles}
              isMobile={isMobile}
              onLanguageChange={(newLang) => handleLanguageChange(1, newLang)}
              onModeChange={(newMode) => handleDisplayModeChange(1, newMode)}
            />

            <SubtitleItem
              subNumber={2}
              title="زیرنویس دو"
              currentLanguage={getCurrentLanguage(2)}
              currentMode={getCurrentMode(2)}
              disabled={!showSubtitles}
              isMobile={isMobile}
              onLanguageChange={(newLang) => handleLanguageChange(2, newLang)}
              onModeChange={(newMode) => handleDisplayModeChange(2, newMode)}
            />
          </div>

          {/* دکمه ریست */}
          <button
            className={`ssp-reset-btn ${!showSubtitles ? "ssp-disabled" : ""}`}
            onClick={(e) => {
              e.stopPropagation();
              if (showSubtitles) {
                handleReset();
              }
            }}
            disabled={!showSubtitles}
          >
            <span className="ssp-reset-icon">↺</span>
            <span>ریست به پیش‌فرض</span>
          </button>
        </div>
      </div>
    </div>,
    getPortalContainer(isIOS, isFullscreen),
  );
};

export default SubtitleSettingsPanel;
